import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';
import { departmentPath, useDepartmentSlug } from '../utils/departmentRouting';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type StorefrontPackage = {
  id: string;
  name: string;
  slug: string;
  sectionLabel?: string | null;
  headline?: string | null;
  blurb?: string | null;
  heroImageUrl?: string | null;
  priceUsd: number | string;
  sortOrder?: number;
  department?: { id: string; name: string; slug: string } | null;
  items?: Array<{
    qty: number;
    unit: 'LB' | 'KG' | 'PACK';
    product?: {
      id?: string;
      name?: string | null;
      salePrice?: number | null;
      pricePerUnit?: number | null;
      isActive?: boolean;
      currentStockLbs?: number | null;
    } | null;
  }>;
};

function toLbs(qty: number, unit: 'LB' | 'KG' | 'PACK') {
  if (unit === 'KG') return qty * 2.20462;
  return qty;
}

function isPackageAvailable(pkg: StorefrontPackage): boolean {
  const needByProduct = new Map<string, number>();
  const stockByProduct = new Map<string, number>();
  for (const row of pkg.items || []) {
    const pid = row.product?.id;
    if (!pid) return false;
    if (row.product?.isActive === false) return false;
    const need = toLbs(Number(row.qty || 0), row.unit);
    if (!Number.isFinite(need) || need <= 0) return false;
    needByProduct.set(pid, (needByProduct.get(pid) ?? 0) + need);
    stockByProduct.set(pid, Number(row.product?.currentStockLbs ?? 0));
  }
  if (!needByProduct.size) return false;
  for (const [pid, needed] of needByProduct) {
    if ((stockByProduct.get(pid) ?? 0) + 1e-9 < needed) return false;
  }
  return true;
}

export default function PackagesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const departmentSlug = useDepartmentSlug();
  const { addLine } = useCart();
  const { formatPrice } = useCurrency();
  const [items, setItems] = useState<StorefrontPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/catalog/packages`);
        if (departmentSlug) {
          url.searchParams.set('departmentSlug', departmentSlug);
        }
        const res = await fetch(
          applyContentLocaleToUrl(
            url.toString(),
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) throw new Error('load');
        const data = (await res.json()) as StorefrontPackage[];
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) {
          setError(t('storefront.packages_load_error', { defaultValue: 'Unable to load packages.' }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departmentSlug, t, i18n.language, i18n.resolvedLanguage]);

  const packageDepartmentGroups = useMemo(() => {
    if (departmentSlug != null) return null;
    const map = new Map<
      string,
      { department: { id: string; name: string; slug: string }; items: StorefrontPackage[] }
    >();
    for (const pkg of items) {
      const dep = pkg.department;
      const slug = dep?.slug || '__';
      if (!map.has(slug)) {
        map.set(slug, {
          department: dep ?? { id: '__', name: 'Other', slug: '__' },
          items: [],
        });
      }
      map.get(slug)!.items.push(pkg);
    }
    return [...map.values()].sort((a, b) =>
      a.department.name.localeCompare(b.department.name),
    );
  }, [departmentSlug, items]);

  const accent = 'var(--storefront-nav-accent, #f97316)';

  const renderPackageCard = (pkg: StorefrontPackage) => {
    const packageAvailable = isPackageAvailable(pkg);
    const hero =
      pkg.heroImageUrl && pkg.heroImageUrl.startsWith('http')
        ? pkg.heroImageUrl
        : pkg.heroImageUrl
          ? `${API_BASE}${pkg.heroImageUrl}`
          : null;
    const listTotal = (pkg.items || []).reduce((sum, row) => {
      const unitPrice = Number(row.product?.salePrice ?? row.product?.pricePerUnit ?? 0);
      const qty = Number(row.qty ?? 0);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) return sum;
      if (!Number.isFinite(qty) || qty <= 0) return sum;
      return sum + unitPrice * qty;
    }, 0);
    return (
      <article
        className="group relative flex min-h-[320px] flex-col overflow-hidden rounded-2xl bg-slate-900 shadow-md ring-1 ring-slate-900/10 transition hover:ring-2 hover:ring-offset-2"
        style={{ ['--tw-ring-color' as string]: accent }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center transition duration-500 group-hover:scale-[1.02]"
          style={
            hero
              ? { backgroundImage: `url(${hero})` }
              : { background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)' }
          }
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-5">
          <div>
            <div className="mt-1">
              <p className="text-lg font-extrabold text-rose-200 line-through decoration-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                {formatPrice(listTotal)}
              </p>
              <p className="text-3xl font-extrabold leading-none text-emerald-300 drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] sm:text-4xl">
                {formatPrice(Number(pkg.priceUsd || 0))}
              </p>
            </div>
            <ul className="mt-3 list-disc space-y-1 pl-4 text-sm font-medium text-white marker:text-white/90 drop-shadow-[0_1px_3px_rgba(0,0,0,0.75)]">
              {(pkg.items?.slice(0, 3) || []).map((row, idx) => (
                <li key={`${pkg.id}-it-${idx}`} className="line-clamp-1">
                  {row.qty} {row.unit} {row.product?.name || t('storefront.package_item_fallback', { defaultValue: 'Item' })}
                </li>
              ))}
              {(!pkg.items || pkg.items.length === 0) ? (
                <li className="line-clamp-1">
                  {t('storefront.package_items_unavailable', { defaultValue: 'Items listed on details page' })}
                </li>
              ) : null}
            </ul>
          </div>
          {pkg.sectionLabel ? (
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/80">
              {pkg.sectionLabel}
            </p>
          ) : null}
          <h2 className="line-clamp-2 text-lg font-bold text-white drop-shadow-sm">{pkg.name}</h2>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              disabled={!packageAvailable}
              onClick={() => addLine({ kind: 'package', packageId: pkg.id, qty: 1 })}
              className="inline-flex w-full justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-500 sm:w-auto"
            >
              {t('storefront.add_to_cart', { defaultValue: 'Add to cart' })}
            </button>
            <button
              type="button"
              onClick={() =>
                navigate(
                  departmentPath(
                    departmentSlug,
                    `/packages/${encodeURIComponent(pkg.slug)}`,
                  ),
                )
              }
              className="inline-flex w-full justify-center rounded-full px-4 py-2 text-sm font-semibold text-white shadow-md sm:w-auto"
              style={{ backgroundColor: accent }}
            >
              {t('storefront.packages_details', { defaultValue: 'Details' })}
            </button>
          </div>
          {!packageAvailable ? (
            <p className="mt-2 text-xs font-semibold text-amber-200">
              {t('storefront.package_unavailable', {
                defaultValue: 'Unavailable package',
              })}
            </p>
          ) : null}
        </div>
      </article>
    );
  };

  return (
    <StorefrontLayout mainClassName="bg-slate-50" contentContained>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:py-14">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-700">
          {t('storefront.packages_kicker', { defaultValue: 'Featured bundles' })}
        </p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {t('storefront.packages_title_prefix', { defaultValue: 'Build flavor from the ' })}
          <span style={{ color: accent }}>{t('storefront.packages_title_accent1', { defaultValue: 'pantry' })}</span>
          {t('storefront.packages_title_mid', { defaultValue: ' out' })}
        </h1>
        <p className="mt-3 max-w-2xl workspace-page-subtitle text-slate-600">
          {t('storefront.packages_subtitle', {
            defaultValue: 'Curated sets of products at a bundle price—add to cart like any other item.',
          })}
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('storefront.packages_info_1_title', { defaultValue: 'What this page is' })}
            </p>
            <p className="mt-1 text-sm text-slate-700">
              {t('storefront.packages_info_1_body', {
                defaultValue: 'Pre-built bundles with one clear package price and item breakdown.',
              })}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('storefront.packages_info_2_title', { defaultValue: 'How to choose' })}
            </p>
            <p className="mt-1 text-sm text-slate-700">
              {t('storefront.packages_info_2_body', {
                defaultValue: 'Open a package to review included products, quantities, and full details.',
              })}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('storefront.packages_info_3_title', { defaultValue: 'Checkout' })}
            </p>
            <p className="mt-1 text-sm text-slate-700">
              {t('storefront.packages_info_3_body', {
                defaultValue: 'Add the package to cart in one click, then continue like a normal order.',
              })}
            </p>
          </div>
        </div>

        {loading && <p className="mt-8 text-sm text-slate-600">{t('common.loading')}</p>}
        {error && (
          <p className="mt-8 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {!loading && !error && items.length === 0 ? (
          <p className="mt-8 text-sm text-slate-600">{t('storefront.packages_empty', { defaultValue: 'No packages yet.' })}</p>
        ) : null}

        {packageDepartmentGroups ? (
          <div className="mt-8 space-y-12">
            {packageDepartmentGroups.map((group) => (
              <div key={group.department.slug}>
                <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 pb-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                      {t('storefront.catalog_department_eyebrow', { defaultValue: 'Department' })}
                    </p>
                    <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900">
                      {group.department.name}
                    </h2>
                  </div>
                  <span className="shrink-0 font-mono text-[11px] font-medium text-slate-400">
                    /{group.department.slug}
                  </span>
                </div>
                <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                  {group.items.map((pkg) => (
                    <React.Fragment key={pkg.id}>{renderPackageCard(pkg)}</React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {items.map((pkg) => (
              <React.Fragment key={pkg.id}>{renderPackageCard(pkg)}</React.Fragment>
            ))}
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}
